import { useEffect, useMemo, useRef, useState } from 'react';
import taxService from '@/services/billing/taxService';
import itemService from '@/services/billing/itemService';
import flatFeesService from '@/services/billing/flatFeesService';
import matterService from '@/services/matter/matterService';
import userService from '@/services/setting/userService';
import { useAbility } from '@/providers/';
import { toast } from 'sonner';
import { KeenIcon, DefaultTooltip } from '@/components';
import { Can } from '@casl/react';
import { ItemsModal } from '@/pages/billing';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { useAuthContext } from '@/auth';
import { Link, useNavigate } from 'react-router-dom';
import { renderMultiAvatars, FiltersToFormData, getAccessToken, decodeHtmlEntities } from '@/utils'
import { createDataTableOptions, createDataTableConfig } from '@/utils';
import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal, useContactOptions, useMatterOptions } from '@/hooks';
import { SelectInput, DateInput, STATUS_INVOICE_OPTIONS, FilterContainer, ColumnToggleDropdown } from '@/components/TableFilter'
import { cn } from '@/lib/utils';
import { goTo403 } from "@/utils/Navigation";
import invoiceService from '@/services/billing/invoice/invoiceService';
import { useUserOptions } from '@/hooks'
import { Container } from '@/components/container';

DataTable.use(DT);
const FlatFees = () => {
  const { logout } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [selectedData, setSelectedData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false)
  const [contactTypeData, setContactTypeData] = useState([])
  const [taxesData, setTaxesData] = useState([])
  const [itemId, setItemId] = useState(null)
  const { ability } = useAbility();
  const loadContactOptions = useContactOptions({ defaultPageSize: 20 });
  const loadMatterOptions = useMatterOptions({ defaultPageSize: 20 });
  const [showFilter, setShowFilter] = useState(false);
  const [filterData, setFilterData] = useState({
    billed_by: null,
    status: null,
    contact_id: null,
    matter_id: null,
    from_date: null,
    to_date: null,
    date_range: null,
  });
  const API_URL = import.meta.env.VITE_APP_BASE_URL;
  const token = getAccessToken();
  const tableRef = useRef(null);
  const filterRef = useRef(filterData);
  const formikRef = useRef();
  const Navigate = useNavigate()
  // const canDeleteFlatFees = ability.can('delete', 'flat-fees');
  const canDeleteFlatFees = ability.can('handle', 'flat-fees');
  const canEditFlatFees = ability.can('handle', 'flat-fees');
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();
  const loadUserOptions = useUserOptions({ defaultPageSize: 20 });

  useEffect(() => {
    filterRef.current = filterData;
  }, [filterData]);

  const badgeClasses = {
    billable: 'badge-success',
    billed: 'badge-info',
    paid: 'badge-primary',
    not_billable: 'badge-danger',
  }

  const columns = [
    // {
    //   title: 'S#',
    //   name: 'serial',
    //   data: null,
    //orderable: true,se,
    //   render: (data, type, row, meta) => meta.row + 1,
    // },
    {
      data: 'item',
      title: 'Item',
      name: 'item',
      orderable: true,
      render: function (data, type, row) {
        return `<div>
        <div class="text-xs my-1">Name: ${row.item?.name ?? '-'}</div>
        <div class="text-xs my-1">Code: ${row.item?.code ?? '-'}</div>
        <div class="text-xs my-1">Price: $ ${row.item?.price ?? '0.00'}</div>
      </div>`;
      }
    },
    {
      data: 'status',
      title: 'Status',
      name: 'status',
      orderable: true,
      render: function (value) {
        return `<span class="badge badge-pill badge-outline ${badgeClasses[value]} gap-1 items-center">
        <span class="badge badge-dot size-1.5 ${badgeClasses[value]}"></span>
        ${value}
      </span>`;
      }
    },
    { data: 'date', title: 'Date', name: 'date' },
    {
      data: 'rate',
      title: 'Rate',
      name: 'rate',
      render: function (data, type, row) {
        return `<div>$${row.rate}</div>`;
      }
    },
    {
      data: 'total',
      title: 'Total',
      name: 'total',
      render: function (data, type, row) {
        return `<div>$${row.total}</div>`;
      }
    },
    { data: 'quantity', title: 'Quantity', name: 'quantity' },
    {
      data: 'contact',
      title: 'Contact',
      name: 'contact',
      orderable: true,
      render: function (data, type, row) { return `<div id="contact-cell-${row.id}"></div>` }
    },
    {
      data: 'matter',
      title: 'Matter',
      name: 'matter',
      orderable: true,
      render: function (data, type, row) {
        return `<div id="matter-cell-${row.id}"></div>`;  // Use string template for HTML
      }
    },
    { data: 'created_at', title: 'Created At', name: 'created_at' },
    {
      title: 'Billed By',
      name: 'billed_by',
      data: null,
      orderable: true,
      searchable: false,
      render: function (data, type, row) {
        return `<div id="billed-by-${row.id}"></div>`;
      }
    },

    // ✅ Extended columns (newly added)
    { data: 'description', title: 'Description', name: 'description' },
    // { data: 'note', title: 'Note', name: 'note' },
    // {
    //   data: 'cost_type',
    //   title: 'Cost Type',
    //   name: 'cost_type',
    //   render: function (data) {
    //     return data ?? '-'; // Show '-' if undefined
    //   }
    // },
    // { data: 'invoice_uuid', title: 'Invoice UUID', name: 'invoice_uuid' },
    // { data: 'item_uuid', title: 'Item UUID', name: 'item_uuid' },
    // { data: 'matter_uuid', title: 'Matter UUID', name: 'matter_uuid' },
    // { data: 'contact_uuid', title: 'Contact UUID', name: 'contact_uuid' },
    // { data: 'billed_by_uuid', title: 'Billed By UUID', name: 'billed_by_uuid' },
    // { data: 'created_by', title: 'Created By', name: 'created_by' },
    // { data: 'updated_at', title: 'Updated At', name: 'updated_at' },
    // { data: 'deleted_at', title: 'Deleted At', name: 'deleted_at' },
    // { data: 'DT_RowIndex', title: 'Row Index', name: 'DT_RowIndex' },
  ];

  const defaultVisibleColumns = [
    // 'serial',
    'item',
    'status',
    'date',
    'rate',
    'total',
    'quantity',
    'contact',
    'matter',
    'created_at',
    'billed_by',
  ];


  const handleSubmit = async (formData) => {
    const isEdit = mode === 'edit';
    const action = isEdit ? itemService.updateItem : itemService.createItem;
    const args = isEdit ? [formData, itemId] : [formData];
    try {
      isEdit ? setLoading(true) : setIsLoading(true);
      const { data, status } = await action(...args);
      if (status) {
        setOpen(false);
        refresh();
        toast.success(`Item ${isEdit ? 'Updated' : 'Created'} Successfully`, {
          description: `${data.name} Item ${isEdit ? 'updated' : 'created'} successfully.`,

        })
      }
    } catch (error) {
      console.error('Error', error)
      if (error.message == '422 Unprocessable Entity. Validation failed.') {
        const validationErrors = error.errors
        const formattedErrors = Object.fromEntries(
          Object.entries(validationErrors).map(([key, value]) => [key, value[0]])
        );

        formikRef.current?.setErrors(formattedErrors);
        toast.error('Validation Error.', {
          description: 'Please check your input. Some fields may be invalid.',

        });
      }
    } finally {
      isEdit ? setLoading(false) : setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    closeModal()
    try {
      const { status } = await flatFeesService.deleteFlatFees(actionId);
      if (status) {
        refresh()
        toast.success('Deleted Flat Fees.', {
          description: 'Flat Fees deleted Successfully.',

        });
      }
    } catch (error) {
      console.error('Error', error)
      if (error.message == "Can't delete: Matter records are related to this FlatFee.") {
        toast.warning('Failed to delete Flat Fees.', {
          description: `Can't delete: Matter records are related to this Flat Fees.`,

        });
        return
      }
      toast.error('Failed to delete Flat Fees.', {
        description: 'An error occurred while deleting Flat Fees. Please try again later.',

      });
    }
  }

  const handleEdit = (id) => {
    Navigate(`/billing/flat-fees/edit/${id}/`)
  }

  const handleModalClose = () => {
    setItemId(null)
    setSelectedData(null)
    setMode('create')
    setOpen(false)
  }

  const handleClone = (id) => {
    Navigate(`/billing/flat-fees/create/${id}/`)
  }

  const renderCustomActions = (row) => {
    const { id } = row;

    return (
      <DropdownMenu>
        {(canDeleteFlatFees || canEditFlatFees) && (
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-gray-600">
              <KeenIcon icon="dots-vertical" className="text-[18px]" />
            </Button>
          </DropdownMenuTrigger>
        )}
        <DropdownMenuContent align="end" className="w-40">
          <Can I="handle" a="flat-fees" ability={ability}>
            <DropdownMenuItem onClick={() => handleEdit(id)}>
              <KeenIcon icon="notepad-edit" className="mr-0.5 h-4 w-4 cursor-pointer" /> Edit
            </DropdownMenuItem>
          </Can>
          <Can I="handle" a="flat-fees" ability={ability}>
            <DropdownMenuItem onClick={() => handleClone(id)}>
              <KeenIcon icon="note-2" className="mr-0.5 h-4 w-4 cursor-pointer" /> Clone
            </DropdownMenuItem>
          </Can>
          <Can I="handle" a="flat-fees" ability={ability}>
            <DropdownMenuItem onClick={() => openModal(id)} className="text-red-600 cusror-pointer">
              <KeenIcon icon="trash" className="mr-0.5 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </Can>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderTaxes = (row) => {
    const { taxes } = row;
    console.log('asd', taxes)
    return (
      <>
        {taxes.map((tax, index) => (
          <DefaultTooltip title={`Rate: ${tax.rate}`} placement="top" className="max-w-full">
            {/* <label className="items-center gap-1 mb-1 form-label">
              <KeenIcon icon="information" />
              Number
            </label> */}
            <span className='mb-1 mr-1 capitalize badge badge-sm badge-pill badge-outline badge-primary'>{tax.name}</span>
          </DefaultTooltip>
        ))}
      </>
    )
  }

  const ajaxConfig = createDataTableConfig('/setup/flat_fees/data_table_list', filterRef, token, logout);

  const options = createDataTableOptions({
    order: [[8, 'desc']],
    initComplete: function () {
      const api = this.api();
      api.columns().every(function () {
        const col = this;
        const name = col.settings()[0].aoColumns[col.index()].name;
        if (name && !defaultVisibleColumns.includes(name)) {
          col.visible(false);
        }
      });
    },
    drawCallback: function () {
      const api = this.api();
      const rows = api.rows({ page: 'current' }).data();

      rows.each((row) => {
        const container = document.getElementById(`action-cell-${row.id}`);
        if (container) {
          if (!container._reactRoot) {
            container._reactRoot = createRoot(container);
          }
          container._reactRoot.render(renderCustomActions(row));
        }
        const contactContainer = document.getElementById(`contact-cell-${row.id}`);
        if (contactContainer) {
          if (!contactContainer._reactRoot) {
            contactContainer._reactRoot = createRoot(contactContainer);
          }
          contactContainer._reactRoot.render(renderContactName(row));
        }
        const matterContainer = document.getElementById(`matter-cell-${row.id}`);
        if (matterContainer) {
          if (!matterContainer._reactRoot) {
            matterContainer._reactRoot = createRoot(matterContainer);
          }
          matterContainer._reactRoot.render(renderMatterName(row));
        }
        const billedContainer = document.getElementById(`billed-by-${row.id}`);
        if (billedContainer) {
          if (!billedContainer._reactRoot) {
            billedContainer._reactRoot = createRoot(billedContainer);
          }
          billedContainer._reactRoot.render(renderMultiAvatars(row.billed_by));
        }
      });
    },
  });

  const resetFilters = () => {
    const cleared = {
      billed_by: null,
      status: null,
      contact_id: null,
      matter_id: null,
      from_date: null,
      to_date: null,
      date_range: null,
    };

    setFilterData(cleared);
    filterRef.current = cleared;
    refresh()
  };

  const handleFilterChange = (key, val) => {
    setFilterData(prev => {
      const updated = { ...prev, [key]: val };
      if (key === 'contact_id') updated.matter_id = null; // recommended
      filterRef.current = updated;
      return updated;
    });
  };

  const handleToggleFilter = () => {
    setShowFilter(prev => !prev)
  }

  const fetchLookup = async (type, ctx = {}) => {
    switch (type) {
      case 'users': {
        const { data } = await userService.getAllUsers();
        return data.map(u => ({ label: `${u.first_name} ${u.last_name}`, value: u.id, email: u.email }));
      }
      case 'matter': {
        const contactId = ctx?.contactId || null;
        if (contactId) {
          const res = await invoiceService.getContactMatterRelatedData('contact', contactId);
          const rows = res?.data?.matters || [];
          return rows.map(m => ({ label: m.name, value: m.id }));
        }
        const matters = await matterService.getAllMatters();
        return (matters?.data || []).map(m => ({ label: m.name, value: m.id }));
      }
      default:
        return [];
    }
  };

  const refresh = () => {
    const table = tableRef.current.dt();
    table.ajax.reload();
  };

  const renderContactName = (row) => {
    console.log('asd', row)
    if (!row.contact) {
      return <span>-</span>;
    }
    const fullName = row.contact.first_name + ' ' + row.contact.last_name;
    const { id } = row.contact;
    return (
      <span onClick={() => { handleNavigateConatct(id) }} className='cursor-pointer link'>{fullName}</span>
    )
  }

  const handleNavigateConatct = (id) => {
    Navigate(`/contacts/${id}/`)
  }

  const handleNavigateMatter = (id) => {
    Navigate(`/matters/detail/${id}/`)
  }

  const renderMatterName = (row) => {
    const { matter } = row;
    const fullName = decodeHtmlEntities(matter?.name || '');

    return (
      <span
        onClick={() => handleNavigateMatter(matter?.id || '')}
        className="cursor-pointer link"
      >
        {fullName}
      </span>
    );
  }

  // useEffect(() => {
  //   getAllTaxes()
  // }, [])


  return (
    <>
      <Container>
        {/* Filters */}
        <FilterContainer
          show={showFilter}
          onApply={() => refresh()}
          onReset={resetFilters}
        >
          <SelectInput
            label="Billed By"
            type="users"
            value={filterData.billed_by}
            onChange={(val) => handleFilterChange('billed_by', val)}
            loadOptions={loadUserOptions}
            pageSize={20}
            customClass="sm:w-[32%] w-full"
          />

          <SelectInput
            // isMulti={true}
            label="Contact"
            type="contact"
            value={filterData.contact_id}
            onChange={(val) => handleFilterChange('contact_id', val)}
            loadOptions={loadContactOptions}
            pageSize={20}
            customClass="sm:w-[32%] w-full"
          />

          <SelectInput
            // isMulti={true}
            label="Matter"
            type="matter"
            value={filterData.matter_id}
            onChange={(val) => handleFilterChange('matter_id', val)}
            loadOptions={loadMatterOptions}
            pageSize={20}
            lookupParams={{ contactId: filterData.contact_id?.value ?? null }}
            customClass="sm:w-[32%] w-full"
          />

          <SelectInput
            label="Status"
            value={filterData.status}
            onChange={(val) => handleFilterChange('status', val)}
            staticOptions={STATUS_INVOICE_OPTIONS}
            customClass="sm:w-[32%] w-full"
          />

          <DateInput
            customClass="sm:w-[32%] w-full"
            label="Created At - (From, To)"
            mode="range"
            value={filterData.date_range}
            onChange={(range) => {
              setFilterData(prev => ({
                ...prev,
                from_date: range?.from ?? null,
                to_date: range?.to ?? null,
                date_range: range,
              }));
            }}
          />
          {/* <DateFilter
            value={filterData.created_at}
            onChange={(date) =>
              setFilterData((prev) => ({
                ...prev,
                created_at: date,
              }))
            }
          /> */}
        </FilterContainer>

        <div className="min-w-full mb-4 card">
          <div className="card-header">
            <div className='flex items-center gap-1'>
              <ColumnToggleDropdown tableRef={tableRef} columns={columns} defaultVisibleColumns={defaultVisibleColumns} />
              {/* <h3 className="card-title">Flat Fees</h3> */}
            </div>

            <div className='flex flex-wrap items-center gap-2'>
              <button className='text-xs btn btn-sm btn-primary btn-outline' onClick={handleToggleFilter}>
                <KeenIcon icon="filter" className="w-fit btn-icon" />
                {showFilter ? 'Hide Advanced Filters' : 'Advanced Filters'}
              </button>
              <div className='flex flex-wrap items-center gap-2'>
                {/* <Can I="handle" a="flat-fees" ability={ability}>
                <Link to='/billing/flat-fees/create' className='text-xs btn btn-sm btn-light'>
                    <KeenIcon icon="plus" className="w-fit btn-icon" />
                    Add New
                </Link>
              </Can> */}
              </div>
            </div>
          </div>


          <DataTable
            id="flat-fees-table"
            ajax={ajaxConfig}
            columns={columns}
            options={options}
            ref={tableRef}
          />

          <ItemsModal
            open={open}
            onCloseModal={handleModalClose}
            handleMode={mode}
            data={selectedData}
            onSubmit={handleSubmit}
            createFieldLoading={isLoading}
            updateFieldLoading={loading}
            // taxesData={taxesData}
            ref={formikRef}
          />

          <ConfirmationModal
            show={isOpen}
            onClose={closeModal}
            onConfirm={handleDelete}
            message="Are you sure you want to delete this Flat Fees? This action is permanent and cannot be undone."
            title="Are You Sure?"
          />
        </div>
      </Container>
    </>
  )
}

export { FlatFees }
